import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import CowAvatar from '../components/CowAvatar';

const TABS = ['Amigos', 'Solicitudes', 'Buscar'];

export default function Friends() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [friendStatuses, setFriendStatuses] = useState({});
  const [loading, setLoading] = useState(false);

  const loadFriends = useCallback(async () => {
    const res = await api.get('/friends');
    setFriends(res.data);
  }, []);

  const loadRequests = useCallback(async () => {
    const res = await api.get('/friends/requests');
    setRequests(res.data);
  }, []);

  useEffect(() => {
    loadFriends();
    loadRequests();
  }, [loadFriends, loadRequests]);

  const handleSearch = async () => {
    if (searchQ.trim().length < 2) return;
    setLoading(true);
    try {
      const res = await api.get(`/users/search?q=${encodeURIComponent(searchQ.trim())}`);
      const users = res.data;
      setSearchResults(users);
      // fetch statuses for each result
      const statuses = {};
      await Promise.all(users.map(async u => {
        try {
          const s = await api.get(`/friends/status/${u.id}`);
          statuses[u.id] = s.data;
        } catch {
          statuses[u.id] = { status: 'NONE' };
        }
      }));
      setFriendStatuses(statuses);
    } finally {
      setLoading(false);
    }
  };

  const sendRequest = async (userId) => {
    await api.post(`/friends/request/${userId}`);
    setFriendStatuses(prev => ({ ...prev, [userId]: { status: 'PENDING', isRequester: true } }));
  };

  const acceptRequest = async (friendshipId) => {
    await api.post(`/friends/accept/${friendshipId}`);
    await loadRequests();
    await loadFriends();
  };

  const removeFriend = async (friendshipId) => {
    await api.delete(`/friends/${friendshipId}`);
    await loadFriends();
  };

  const getStatusButton = (user) => {
    const s = friendStatuses[user.id];
    if (!s || s.status === 'NONE') {
      return <button className="action-btn add" onClick={() => sendRequest(user.id)}>+ Agregar</button>;
    }
    if (s.status === 'ACCEPTED') {
      return <span className="status-badge accepted">Amigos ✓</span>;
    }
    if (s.status === 'PENDING' && s.isRequester) {
      return <span className="status-badge pending">Solicitud enviada</span>;
    }
    if (s.status === 'PENDING' && !s.isRequester) {
      return <button className="action-btn add" onClick={() => acceptRequest(s.friendshipId)}>Aceptar</button>;
    }
    return null;
  };

  return (
    <div className="friends-page">
      <h2 className="page-title">Amigos</h2>

      <div className="tabs">
        {TABS.map((t, i) => (
          <button key={i} className={`tab ${tab === i ? 'active' : ''}`} onClick={() => setTab(i)}>
            {t}
            {i === 1 && requests.length > 0 && <span className="badge">{requests.length}</span>}
          </button>
        ))}
      </div>

      {tab === 0 && (
        <div className="list">
          {friends.length === 0 && <p className="empty">Aún no tienes amigos agregados.</p>}
          {friends.map(f => (
            <div key={f.userId} className="friend-card clickable-card" onClick={() => navigate(`/user/${f.userId}`)}>
              <CowAvatar seed={f.profilePicture || f.userId} size={42} />
              <div className="info">
                <span className="name">{f.userName}</span>
                <span className="email">{f.email}</span>
              </div>
              <span className="chevron">›</span>
            </div>
          ))}
        </div>
      )}

      {tab === 1 && (
        <div className="list">
          {requests.length === 0 && <p className="empty">No tienes solicitudes pendientes.</p>}
          {requests.map(r => (
            <div key={r.friendshipId} className="friend-card">
              <CowAvatar seed={r.profilePicture || r.userId} size={42} />
              <div className="info">
                <span className="name">{r.userName}</span>
              </div>
              <div className="actions">
                <button className="action-btn add" onClick={() => acceptRequest(r.friendshipId)}>Aceptar</button>
                <button className="action-btn remove" onClick={() => removeFriend(r.friendshipId)}>Rechazar</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 2 && (
        <div className="search-tab">
          <div className="search-row">
            <input
              className="search-input"
              type="text"
              placeholder="Buscar por nombre..."
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
            <button className="search-btn" onClick={handleSearch} disabled={loading}>
              {loading ? '...' : 'Buscar'}
            </button>
          </div>
          <div className="list">
            {searchResults.length === 0 && !loading && searchQ.length > 0 && (
              <p className="empty">Sin resultados.</p>
            )}
            {searchResults.map(u => (
              <div key={u.id} className="friend-card">
                <div className="clickable-avatar" onClick={() => navigate(`/user/${u.id}`)}>
                  <CowAvatar seed={u.profilePicture || u.id} size={42} />
                </div>
                <div className="info clickable-name-text" onClick={() => navigate(`/user/${u.id}`)}>
                  <span className="name">{u.name}</span>
                </div>
                <div className="actions">
                  {getStatusButton(u)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <style jsx>{`
        .friends-page {
          max-width: 600px;
          margin: 0 auto;
          padding: 1.5rem 1rem 80px;
        }

        .page-title {
          color: var(--text-primary);
          font-size: 1.5rem;
          margin-bottom: 1.25rem;
        }

        .tabs {
          display: flex;
          border-bottom: 2px solid var(--border-color);
          margin-bottom: 1.25rem;
        }

        .tab {
          flex: 1;
          padding: 0.6rem 0;
          background: none;
          border: none;
          border-bottom: 2px solid transparent;
          margin-bottom: -2px;
          color: var(--text-muted);
          font-size: 0.95rem;
          cursor: pointer;
          position: relative;
          transition: color 0.15s;
        }

        .tab.active {
          color: var(--accent-color);
          border-bottom-color: var(--accent-color);
          font-weight: 600;
        }

        .badge {
          display: inline-block;
          background: var(--accent-color);
          color: white;
          font-size: 0.65rem;
          font-weight: 700;
          border-radius: 999px;
          padding: 1px 5px;
          margin-left: 4px;
          vertical-align: middle;
        }

        .list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .empty {
          color: var(--text-muted);
          text-align: center;
          padding: 2rem 0;
        }

        .friend-card {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          background: var(--bg-secondary);
          border-radius: 10px;
          padding: 0.75rem 1rem;
          box-shadow: 0 1px 4px var(--shadow-color);
        }

        .clickable-card {
          cursor: pointer;
          transition: background 0.15s;
        }

        .clickable-card:hover { background: var(--bg-hover, var(--bg-primary)); }

        .chevron {
          color: var(--text-muted);
          font-size: 1.3rem;
          line-height: 1;
        }

        .clickable-avatar {
          cursor: pointer;
          transition: opacity 0.15s;
          flex-shrink: 0;
        }

        .clickable-avatar:hover { opacity: 0.8; }

        .clickable-name-text {
          cursor: pointer;
        }

        .clickable-name-text:hover .name { color: var(--accent-color); }

        .info {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
        }

        .name {
          color: var(--text-primary);
          font-weight: 500;
          font-size: 0.95rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .email {
          color: var(--text-muted);
          font-size: 0.8rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .actions {
          display: flex;
          gap: 0.5rem;
          flex-shrink: 0;
        }

        .action-btn {
          padding: 0.4rem 0.85rem;
          border-radius: 8px;
          border: none;
          font-size: 0.85rem;
          font-weight: 500;
          cursor: pointer;
          transition: opacity 0.15s;
        }

        .action-btn:hover { opacity: 0.85; }

        .action-btn.add {
          background: var(--accent-color);
          color: white;
        }

        .action-btn.remove {
          background: var(--danger-color);
          color: white;
        }

        .status-badge {
          font-size: 0.82rem;
          padding: 0.35rem 0.7rem;
          border-radius: 8px;
          font-weight: 500;
        }

        .status-badge.accepted {
          background: #d1fae5;
          color: #065f46;
        }

        .status-badge.pending {
          background: var(--bg-primary);
          color: var(--text-muted);
          border: 1px solid var(--border-color);
        }

        /* Search tab */
        .search-tab {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .search-row {
          display: flex;
          gap: 0.5rem;
        }

        .search-input {
          flex: 1;
          padding: 0.75rem 1rem;
          border: 1.5px solid var(--border-color);
          border-radius: 10px;
          background: var(--bg-secondary);
          color: var(--text-primary);
          font-size: 0.95rem;
        }

        .search-input:focus {
          outline: none;
          border-color: var(--accent-color);
        }

        .search-btn {
          padding: 0.75rem 1.25rem;
          background: var(--accent-color);
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
        }

        .search-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
}
